import { AgGridAngular } from '@ag-grid-community/angular';
import {
    CellMouseOutEvent,
    CellMouseOverEvent,
    CellValueChangedEvent,
    ColDef,
    ColGroupDef,
    Column,
    ColumnMovedEvent,
    ColumnResizedEvent,
    ExcelExportParams,
    GetLocaleTextParams,
    GetRowIdParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IFilter,
    IRowModel,
    PostProcessPopupParams,
    ProcessCellForExportParams,
    ProcessRowGroupForExportParams,
    RowClassParams,
    RowDoubleClickedEvent,
    RowNode,
    SortChangedEvent,
    ValueFormatterParams
} from '@ag-grid-community/core';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import {
    AgGridApiBusService,
    BooleanIconRendererComponent,
    CommonGridAgGridApiService,
    CommonGridColumnApiService,
    CommonGridStandardRowHeight,
    ExcelExporterService,
    GridTooltipComponent,
    GroupRowData,
    GroupRowRendererComponent,
    GroupRowRendererParams,
    GroupRowRendererService,
    PCustomDateFilterComponent
} from '@frontend-site/grid/grid-utils';
import { _difference, _size, removeHTMLTags } from '@frontend-site/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { FrameworkComponentsMap } from './common-grid.types';

@Component({
    selector: 'common-grid',
    template: `
        <ag-grid-angular
            [gridOptions]="gridOptions"
            [columnDefs]="columnDefs"
            [defaultColDef]="defaultColDef"
            [excelStyles]="excelStyles"
            class="ag-theme-balham"
            [ngClass]="agGridClassNames"
            [class.with-footer]="footerEnabled"
            [rowData]="rowData"
            [icons]="icons"
            (filterChanged)="onFilterChanged()"
            (gridReady)="onReady($event)"
            (rowDataUpdated)="rowDataUpdated()"
            (selectionChanged)="selectionChanged()"
            (rowSelected)="onRowSelected($event)"
            [rowClassRules]="rowClassRules"
            [components]="frameworkComponents"
            (cellValueChanged)="cellValueChanged.emit($event)"
            (cellEditingStopped)="cellEditingStopped.emit($event)"
            [stopEditingWhenCellsLoseFocus]="true"
            (columnMoved)="columnMoved($event)"
            (columnRowGroupChanged)="columnRowGroupChanged($event)"
            (modelUpdated)="displayedRowsChanged()"
            [class.grouping-mode]="isOnGroupingMode"
            [groupRowRendererParams]="getGroupRowRendererParams(this.columnDefs | extract: groupsPredicate)"
            (sortChanged)="onSortChanged($event)"
            [domLayout]="gridLayout"
            (rowDoubleClicked)="onRowDoubleClicked($event)"
            (cellMouseOver)="onCellMouseOver($event)"
            (cellMouseOut)="onCellMouseOut($event)"
        >
        </ag-grid-angular>

        <common-grid-footer
            *ngIf="footerEnabled"
            [totalSelected]="selectedIdsCount$ | async"
            [totalEntities]="rowCount"
            [isSelectEnable]="isSelectEnable"
            [isReloadEnabled]="enableRefresh"
            (reloadRows)="reloadRows()"
        >
            <ng-content></ng-content>
            <ng-content select="[additionalData]"></ng-content>
        </common-grid-footer>
    `,
    styleUrls: ['./common-grid.component.scss'],
    providers: [AgGridApiBusService, CommonGridColumnApiService, CommonGridAgGridApiService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'CommonGrid'
})
export class CommonGridComponent<TData = unknown> implements OnInit, AfterViewInit {
    public gridLayout: AgGridAngular['domLayout'];
    @ViewChild(AgGridAngular) public readonly agGrid;

    @Output() reloadGrid = new EventEmitter<void>();
    @Output() gridReady = new EventEmitter<void>();
    @Output() cellValueChanged = new EventEmitter<CellValueChangedEvent<TData>>();
    @Output() cellEditingStopped = new EventEmitter<CellValueChangedEvent<TData>>();
    @Output() rowSelected = new EventEmitter<RowSelectedEvent>();
    @Output() sortChanged = new EventEmitter<SortChangedEvent<TData>>();
    @Output() columnsMoved = new EventEmitter<ColumnMovedEvent<TData>>();
    @Output() displayedRowsCountChanged = new EventEmitter<number>();
    @Output() rowDoubleClicked = new EventEmitter<RowDoubleClickedEvent<TData>>();
    @Output() rowsDataUpdated = new EventEmitter<GridApi>();
    @Output() cellMouseOvered = new EventEmitter<CellMouseOverEvent<TData>>();
    @Output() cellMouseLeaved = new EventEmitter<CellMouseOutEvent<TData>>();

    @Input() enableRefresh = false;
    @Input() footerEnabled = true;
    @Input() sizeColumnsToFit = false;
    @Input() refreshGridAfterRowUpdate = false;
    @Input() showGroupRowTooltip = false;
    @Input() autoHeight: boolean = false;
    @Input() excelStyles = this.excelExporterService.excelStyles;
    @Input() classNames: string;
    @Input() redrawRowsAfterRowDataChanged = false;
    @Input() autoSelectFirstRow = false;
    @Input() disableRule = (params: RowClassParams): boolean => false;
    @Input() getRowNodeId = (d: TData): string => (d as Partial<{ id: string }>)?.id ?? null;
    @Input() rowGroupPanelShow = 'always';

    @Input() set standardRowHeight(selectedRowHeight: CommonGridStandardRowHeight) {
        this._standardRowHeight = selectedRowHeight;
        if (this._gridOptions) {
            this._gridOptions.rowHeight = selectedRowHeight.height;
            if (this.gridApi) {
                this.gridApi.resetRowHeights();
            }
        }
    }

    @Input() set columnDefs(columnDefs: ColDef[]) {
        this._columnDefs = columnDefs;
        this.updateColumnDefs();
    }

    @Input() set filters(filters: Map<string, FiltersModel>) {
        this._filters = filters;
        if (this._filters && this.gridApi) {
            this.setFilter(this._filters);
        }
    }

    @Input() set shouldAddTooltip(shouldAddTooltip: boolean) {
        this._shouldAddTooltip = shouldAddTooltip;
        this.updateColumnDefs();
    }

    @Input() set shouldShowTooltipForFields(shouldShowTooltipForFields: Map<string, boolean>) {
        this._shouldShowTooltipForFields = shouldShowTooltipForFields;
        this.updateColumnDefs();
    }

    @Input()
    public set gridOptions(options: GridOptions) {
        this._gridOptions = {
            ...this.defaultOptions,
            ...options
        };
    }

    @Input() set defaultColDef(colDef: ColDef) {
        this._defaultColDef = {
            ...this._defaultColDef,
            ...colDef
        };
    }

    @Input() set frameworkComponents(comps) {
        this._frameworkComponents = { ...this._frameworkComponents, ...comps };
    }

    @Input() set rowData(rows: TData[] | null) {
        if (this.refreshGridAfterRowUpdate) {
            this.clearGrid(rows);
        }

        this.rows = rows;
    }

    @Input() initialSelectedRows: number[] | string[];

    @Input() set selectRowAndFocus(nodeId: number | string) {
        this.changeSelectedRow([nodeId]);
        this.focusRow(nodeId);
    }

    public isOnGroupingMode?: boolean;
    public gridApi: GridApi;

    public rowClassRules = {
        'p-disabled-ag-grid': (params: RowClassParams) => this.disableRule(params)
    };

    public icons = {
        // The svn icon is declared inline since we need ag-grid affects the colors
        cancel: `<svg viewBox="0 0 24 24" stroke="currentColor" fill="none"  stroke-width="1" xmlns="http://www.w3.org/2000/svg">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>`
    };

    private rows: TData[] | null;
    private selectedIdsSubject = new BehaviorSubject<number[] | string[]>(null);
    private selectedRowsNodeIdForChange: number[] | string[];
    private _filters: Map<string, FiltersModel>;
    private _shouldAddTooltip = true;
    private _shouldShowTooltipForFields?: Map<string, boolean>;
    private _columnDefs: ColDef[];
    private _standardRowHeight: CommonGridStandardRowHeight;
    private _defaultColDef: ColDef = {
        resizable: true,
        sortingOrder: ['asc', 'desc'],
        sortable: true,
        enableRowGroup: true,
        menuTabs: ['filterMenuTab'],
        tooltipComponent: 'customTooltip',
        filterParams: {
            buttons: ['apply', 'reset'],
            closeOnApply: true,
            suppressAndOrCondition: true,
            showTooltips: true
        },
        keyCreator: (params) => (params.value == null ? '' : params.value)
    };

    private _frameworkComponents: FrameworkComponentsMap = {
        customTooltip: GridTooltipComponent,
        agDateInput: PCustomDateFilterComponent,
        groupRowInnerRenderer: GroupRowRendererComponent,
        booleanRenderer: BooleanIconRendererComponent
    };

    private defaultOptions: GridOptions = {
        rowGroupPanelShow: this.rowGroupPanelShow,
        rowSelection: 'multiple',
        suppressCellFocus: true,
        suppressContextMenu: true,
        isRowSelectable: (rowNode) => rowNode.data,
        suppressDragLeaveHidesColumns: true,
        suppressMakeColumnVisibleAfterUnGroup: true,
        suppressRowGroupHidesColumns: true,
        rowMultiSelectWithClick: true,
        groupDisplayType: 'groupRows',
        isGroupOpenByDefault: () => true,
        initialGroupOrderComparator: ({ nodeA, nodeB }) => {
            const columnSortOrder = nodeA?.rowGroupColumn?.getSort();
            if (columnSortOrder && columnSortOrder === 'desc') {
                return 0;
            }
            return nodeA.key && nodeB.key ? nodeA.key.localeCompare(nodeB.key) : 0;
        },
        tooltipShowDelay: 1000,
        suppressMenuHide: true,
        enableCellTextSelection: true,
        getRowId: (params: GetRowIdParams<TData>) => this.getRowNodeId(params.data),
        postProcessPopup: (params: PostProcessPopupParams) => {
            if (params.type !== 'columnMenu') {
                return;
            }

            const filterPopupLeftBorder = parseInt(params.ePopup.style.left.slice(0, -2));
            const filterPopupWidth = params.column.getUserProvidedColDef().filter == 'agDateColumnFilter' ? 262 : 220;
            const gridRightBorder = this.gridRef.nativeElement.offsetLeft + this.gridRef.nativeElement.offsetWidth;
            const delta = filterPopupLeftBorder + filterPopupWidth - gridRightBorder;

            params.ePopup.style.left = delta > 0 ? filterPopupLeftBorder - delta + 'px' : params.ePopup.style.left;
            params.ePopup.style.top = (this.gridApi as any).gridOptionsWrapper.getRowGroupPanelShow() == 'always' ? '68.5px' : '34.5px';
        },
        getLocaleText: (params: GetLocaleTextParams) => {
            return this.isTranslatable(params.key) ? this.translate.instant(`gridFilters.${params.key}`) : params.defaultValue;
        },
        onColumnResized: (event: ColumnResizedEvent) => {
            if (this._defaultColDef.wrapText || this._columnDefs.findIndex((col) => col.wrapText) !== -1) {
                this.gridApi?.resetRowHeights();
            }
        }
    };

    private textColumnFilterOption = [
        'contains',
        'notContains',
        'equals',
        'notEqual',
        'startsWith',
        'endsWith',
        {
            displayKey: 'emptyIndication',
            displayName: 'Empty',
            test(filterValue, cellValue) {
                return !cellValue;
            },
            hideFilterInput: true
        },
        {
            displayKey: 'notEmptyIndication',
            displayName: 'Not empty',
            test(filterValue, cellValue) {
                return cellValue;
            },
            hideFilterInput: true
        }
    ];

    private _gridOptions: GridOptions = this.defaultOptions;

    private datefilterParams = {
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string): number => {
            const dateAsString = cellValue;
            if (dateAsString == null) return -1;
            const cellDate = new Date(dateAsString);
            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
                return 0;
            }
            if (cellDate < filterLocalDateAtMidnight) {
                return -1;
            }
            if (cellDate > filterLocalDateAtMidnight) {
                return 1;
            }
            return 0;
        }
    };

    @Output('selectedIdsCount') selectedIdsCount$ = this.selectedIdsSubject.pipe(map((ids: number[] | string[]) => _size(ids)));
    @Output('selectedIds') selectedIds$ = this.selectedIdsSubject;

    constructor(
        private translate: TranslateService,
        private excelExporterService: ExcelExporterService,
        private gridRef: ElementRef,
        private groupRowRendererService: GroupRowRendererService,
        private agGridApiBusService: AgGridApiBusService,
        public readonly gridApiService: CommonGridAgGridApiService,
        public readonly columnGridApiService: CommonGridColumnApiService
    ) {}

    ngOnInit(): void {
        this.gridLayout = this.autoHeight ? 'autoHeight' : 'normal';
    }

    ngAfterViewInit(): void {
        this.agGridApiBusService.initAgGridComponent(this.agGrid);
    }

    public getGroupRowRendererParams(data: unknown): GroupRowRendererParams {
        const groupRowData = data as GroupRowData[];
        return {
            innerRenderer: 'groupRowInnerRenderer',
            fieldsKeys: new Map(groupRowData.map((groupData) => [groupData.field, groupData])),
            showGroupTooltip: this.showGroupRowTooltip
        };
    }

    public groupsPredicate: (colDef: ColDef) => GroupRowData = (colDef) => ({
        field: colDef.field,
        visibleName: colDef.headerName,
        valueFormatter: colDef.valueFormatter
    });

    public get agGridClassNames(): string {
        return this.classNames + ' ' + (this.standardRowHeight?.class || '');
    }

    public get standardRowHeight(): CommonGridStandardRowHeight {
        return this._standardRowHeight;
    }

    public get gridOptions(): GridOptions {
        return this._gridOptions;
    }

    public get defaultColDef(): ColDef {
        return this._defaultColDef;
    }

    public get frameworkComponents() {
        return this._frameworkComponents;
    }

    public get rowData() {
        return this.rows;
    }

    public get rowCount() {
        let count = 0;
        if (this.gridApi) {
            if (!this.isGridContainsGroups()) {
                return this.gridApi.getDisplayedRowCount();
            }

            let currentModel: IRowModel = this.gridApi.getModel();
            (currentModel as any)?.rowsToDisplay?.forEach((currentRow) => {
                if (currentRow?.allChildrenCount && currentRow?.level === 0) {
                    count += currentRow.allChildrenCount;
                }
            });
        }
        return count;
    }

    public get isSelectEnable(): boolean {
        return this._gridOptions?.rowSelection == 'multiple' && !this._gridOptions?.suppressRowClickSelection;
    }

    public get selectedRows(): any[] {
        return this.gridApi?.getSelectedRows();
    }

    public get columnDefs(): ColDef[] {
        return this._columnDefs;
    }

    public getRows(): TData[] {
        return this.rows;
    }

    public selectionChanged(): void {
        const selectedIds: number[] | string[] = this.gridApi
            ?.getSelectedNodes()
            .map((rowNode) => this.getRowNodeId?.(rowNode.data) || rowNode.id);
        this.selectedIdsSubject.next(selectedIds);
    }

    public onRowSelected({ node }: { node: RowNode }): void {
        this.rowSelected.emit({ isSelected: node.isSelected(), data: node.data });
    }

    public onSortChanged(data: SortChangedEvent<TData>) {
        this.sortChanged.emit(data);
    }

    public columnRowGroupChanged({ columns }: { columns: Column[] }) {
        this.isOnGroupingMode = columns?.length > 0;
    }

    public onReady({ api }: GridReadyEvent): void {
        this.gridApi = api;

        if (this.initialSelectedRows) {
            this.changeSelectedRow(this.initialSelectedRows);
        }
        if (this.sizeColumnsToFit) {
            this.gridApi.sizeColumnsToFit();
        }
        if (!!this.selectedRowsNodeIdForChange) {
            this.changeSelectedRow(this.selectedRowsNodeIdForChange);
        }
        if (this._defaultColDef.wrapText || this._columnDefs.findIndex((col) => col.wrapText) !== -1) {
            this.gridApi?.resetRowHeights();
        }
        this.rowDataUpdated();
        this.isOnGroupingMode = api
            .getColumnDefs()
            .filter(isColDef)
            .some((col: ColDef<TData>) => !!col?.rowGroup);
        this.gridReady.emit();

        if (this._filters && this.gridApi) {
            this.setFilter(this._filters);
        }
    }

    public focusRow(rowNodeId): void {
        this.gridApi?.ensureIndexVisible(this.gridApi.getRowNode(rowNodeId)?.rowIndex);
    }

    public focusRowByIndex(rowIndex): void {
        this.gridApi?.ensureIndexVisible(rowIndex);
    }

    public exportGrid(exportGridParams?: ExcelExportParams, filteredColumns?: string[]): void {
        const exportParams: ExcelExportParams = {
            ...this.getExportGridDefaultValues(),
            processRowGroupCallback: this.getProcessRowGroupCallback(false),
            processCellCallback: this.getProcessCellCallback(),
            ...exportGridParams
        };

        if (filteredColumns) {
            const columnsNames = this.columnDefs.map((c) => c.field);
            exportParams.columnKeys = columnsNames.filter((c) => filteredColumns.includes(c));
        }

        if (exportGridParams.onlySelected) {
            this.exportOnlySelectedDataAsExcel(exportParams);
        } else {
            this.gridApi.exportDataAsExcel(exportParams);
        }
    }

    public columnsChanged(selectedColumns: string[]): void {
        const allColumns = this.columnDefs.map((colDef: ColDef) => colDef.field);
        const hiddenColumns = _difference(allColumns, selectedColumns);
        this.columnGridApiService.setColumnsVisible(selectedColumns, true);
        this.columnGridApiService.setColumnsVisible(hiddenColumns, false);
    }

    public rowDataUpdated(): void {
        if (this.redrawRowsAfterRowDataChanged) {
            this.gridApi?.redrawRows();
        }

        if (this._defaultColDef.wrapText || this._columnDefs.findIndex((col) => col.wrapText) !== -1) {
            this.gridApi?.resetRowHeights();
        }

        if (this.autoSelectFirstRow) {
            this.gridApi?.expandAll();
            this.selectFirstRow();
        }

        this.rowsDataUpdated.emit(this.gridApi);
    }

    public deselectRow(rowId: string): void {
        const rowNode = this.gridOptions.api.getRowNode(rowId);
        rowNode?.setSelected(false);
    }

    public deselectRows(): void {
        this.gridApi.deselectAll();
    }

    public onFilterChanged = (): void => {
        if (this.gridApi) {
            this.gridApi.deselectAll();
        }
    };

    public displayedRowsChanged: () => void = () => {
        this.displayedRowsCountChanged.emit(this.rowCount);
    };

    public selectFirstRow = (): void => {
        const groupLinesCountInTopOfGrid = this.getCountOfGroupingRows();
        this.gridApi?.getDisplayedRowAtIndex(groupLinesCountInTopOfGrid)?.setSelected(true);
    };

    public reloadRows(): void {
        this.reloadGrid.emit();
    }

    public selectSingleRowByRowIndex(index: number): void {
        this.gridApi?.deselectAll();
        this.gridApi?.getDisplayedRowAtIndex(index)?.setSelected(true);
    }

    public selectSingleRowById(id: number): void {
        this.gridApi?.deselectAll();
        this.selectRow(id);
    }

    public expandGroup(nodeId: string): void {
        this.gridApi?.getRowNode(nodeId)?.parent.setExpanded(true);
    }

    public columnMoved($event: ColumnMovedEvent<TData>): void {
        this.columnsMoved.emit($event);
    }

    public onRowDoubleClicked($event: RowDoubleClickedEvent<TData>): void {
        this.rowDoubleClicked.emit($event);
    }

    public onCellMouseOver(event: CellMouseOverEvent<TData>): void {
        this.cellMouseOvered.emit(event);
    }

    public onCellMouseOut(event: CellMouseOutEvent<TData>): void {
        this.cellMouseLeaved.emit(event);
    }

    public deselectFilteredRows(): void {
        this.gridApi
            ?.getSelectedNodes()
            .filter((node) => !node.displayed)
            .forEach((node) => node.setSelected(false));
    }

    private clearGrid(rows: TData[]): void {
        if (this.gridApi && this.rows !== rows) {
            this.gridApi.setRowData([]);
        }
    }

    private isGridContainsGroups(): boolean {
        const columnsDef: (ColDef | ColGroupDef)[] = this.gridApi?.getColumnDefs();
        if (columnsDef) {
            for (let currentColumn of columnsDef) {
                if (currentColumn && isColDef(currentColumn) && currentColumn.rowGroup) {
                    return true;
                }
            }
        }
        return false;
    }

    private updateColumnDefs(): void {
        this._columnDefs.map((col, i) => {
            if (col.cellRenderer === 'statusRendererComponent') {
                col.resizable = false;
                col.width = 162;
            } else if (col.filter !== 'agNumberColumnFilter' && !col.tooltipValueGetter) {
                const shouldShowTooltipForField = this._shouldShowTooltipForFields
                    ? this._shouldShowTooltipForFields.get(col.field)
                    : undefined;
                if (shouldShowTooltipForField !== undefined) {
                    col.tooltipField = shouldShowTooltipForField ? col.field : undefined;
                } else {
                    col.tooltipField = this._shouldAddTooltip ? col.field : undefined;
                }
            }

            if (!col.minWidth) {
                if (typeof col.cellRenderer === 'string' && col.cellRenderer.toLowerCase().includes('icon')) {
                    col.minWidth = 40;
                } else {
                    col.minWidth = 55;
                }
            }

            if (col.filter === 'agTextColumnFilter') {
                if (col.filterParams) {
                    col.filterParams.filterOptions = this.textColumnFilterOption;
                } else {
                    col.filterParams = {
                        filterOptions: this.textColumnFilterOption
                    };
                }
            }

            if (col.filter === 'agDateColumnFilter') {
                if (!col.filterParams) {
                    col.filterParams = this.datefilterParams;
                }
            }

            col.headerTooltip = col.headerTooltip || col.headerName;
            this._columnDefs[i] = col;
        });
    }

    private changeSelectedRow(selectedRowNodeIdsForChange) {
        if (selectedRowNodeIdsForChange) {
            this.gridApi?.deselectAll();
            this.selectRowsInGrid(selectedRowNodeIdsForChange);
        }
    }

    private selectRowsInGrid(selectedRowsNodeIdForChange) {
        selectedRowsNodeIdForChange.forEach((selectedRowNodeId) => this.selectRow(selectedRowNodeId));
    }

    private selectRow(selectedRowNodeId) {
        return this.gridApi?.getRowNode(selectedRowNodeId)?.setSelected(true);
    }

    private extractedFilteredColumns(filteredColumns: string[]) {
        const columnsNames = this.gridApi.getColumnDefs().map((colDef: ColDef) => colDef.field);
        return _difference(columnsNames, filteredColumns);
    }

    private getProcessRowGroupCallback(onlySelected: boolean) {
        return (params: ProcessRowGroupForExportParams): string => {
            const groupValue = this.getGroupRowValue(params);
            let childrenCount: number;
            if (onlySelected) {
                childrenCount = this.getNodeChildrenCount(params.api.getSelectedNodes(), params.node.id);
            } else {
                childrenCount = params.node.allChildrenCount;
            }
            return `${params.node.rowGroupColumn?.getColDef().headerName}: ${groupValue} (${childrenCount})`;
        };
    }

    private getGroupRowValue({ node }: ProcessRowGroupForExportParams) {
        return this.groupRowRendererService.getGroupRowValue({ value: node.key }, node.rowGroupColumn.getColDef().valueFormatter);
    }

    private getNodeChildrenCount(selectedNodes: RowNode[], parentId: string) {
        return selectedNodes.filter((node) => node.parent?.id === parentId).length;
    }

    private getProcessCellCallback(): (params: ProcessCellForExportParams) => string {
        return (params) => {
            const colDef = params.column.getColDef();
            const node = params.node;
            if (node?.data?.changedField === 'Description') {
                if (['oldValue', 'newValue'].includes(params.column.getId()) && params.value) {
                    return removeHTMLTags(params.value);
                }
            }
            if (colDef.valueFormatter) {
                if (typeof colDef.valueFormatter === 'string') {
                    return colDef.valueFormatter;
                }

                const valueFormatterParams: ValueFormatterParams = {
                    ...params,
                    data: node.data,
                    node,
                    colDef: params.column.getColDef()
                };
                return colDef.valueFormatter(valueFormatterParams);
            }
            return this.shouldGetParamsValue(node, colDef) ? params.value : params.node?.data?.[colDef.field];
        };
    }

    private shouldGetParamsValue(node: RowNode, colDef: ColDef): boolean {
        return node.group || !!colDef.valueGetter || !colDef.cellRenderer;
    }

    private getCountOfGroupingRows(): number {
        return this.columnDefs.filter((column) => column.rowGroup || column.rowGroupIndex).length;
    }

    private isTranslatable(key: string): boolean {
        return this.translate.instant(`gridFilters.${key}`) != 'gridFilters.' + key;
    }

    private getCheckboxAndHiddenColumnsIds(): string[] {
        const ids: string[] = this.gridApi
            .getColumnDefs()
            .filter((colDef: ColDef<TData> | ColGroupDef<TData>) => isColDef(colDef))
            .filter((colDef: ColDef<TData>) => colDef?.hide)
            .map((colDef: ColDef<TData>) => colDef?.field);
        ids.push(CommonGridEnum.ROW_CHECKBOX);
        return ids;
    }

    private getExportGridDefaultValues(): ExcelExportParams {
        const fileName = 'Export.xlsx';
        const sheetName = 'Sheet1';
        return {
            columnWidth: 150,
            onlySelected: false,
            fileName,
            sheetName,
            customHeader: [],
            columnKeys: this.getDefaultExportColumnKeys()
        };
    }

    private getDefaultExportColumnKeys(): string[] {
        const filteredColumns: string[] = this.getCheckboxAndHiddenColumnsIds();
        return this.extractedFilteredColumns(filteredColumns);
    }

    private exportOnlySelectedDataAsExcel(exportParams: ExcelExportParams): void {
        try {
            this.markRowGroupOfSelectedNodesAsSelected(true);
            exportParams.processRowGroupCallback = this.getProcessRowGroupCallback(true);
            this.gridApi.exportDataAsExcel(exportParams);
        } finally {
            this.markRowGroupOfSelectedNodesAsSelected(false);
        }
    }

    private markRowGroupOfSelectedNodesAsSelected(shouldMarkAsSelected: boolean): void {
        this.gridApi
            ?.getSelectedNodes()
            .filter((node: RowNode) => node.parent?.leafGroup && node.parent.isSelected() !== shouldMarkAsSelected)
            .forEach((node: RowNode) => this.selectRowGroup(node.parent, shouldMarkAsSelected));
    }

    private selectRowGroup(groupRow: RowNode, shouldMarkAsSelected: boolean): void {
        groupRow.setRowSelectable(shouldMarkAsSelected);
        groupRow.selectThisNode(shouldMarkAsSelected);
    }

    private setFilter(filters: Map<string, FiltersModel>): void {
        try {
            const keys = filters.keys();
            Array.from(keys).forEach((key) => {
                this.gridApi.getFilterInstance(key, (filterComponent: IFilter | null) => {
                    const filter = filters.get(key);
                    filterComponent?.setModel({
                        type: filter.condition,
                        filter: [filter.value],
                        values: [filter.value]
                    });
                    this.gridApi.onFilterChanged();
                });
            });
        } catch (e) {}
    }
}

export enum CommonGridEnum {
    ROW_CHECKBOX = 'rowCheckbox'
}

export interface RowSelectedEvent {
    isSelected: boolean;
    data: object;
}

export interface FiltersModel {
    condition: string;
    value: unknown;
}

export function isColDef(definition: ColDef | ColGroupDef): definition is ColDef {
    return !isColGroupDef(definition);
}

export function isColGroupDef(definition: ColDef | ColGroupDef): definition is ColGroupDef {
    return !!definition && 'children' in definition;
}
